import clsx from 'clsx';

import { DownloadIcon } from '~components/icons/DownloadIcon';
import { FolderSymlinkIcon } from '~components/icons/FolderSymlinkIcon';
import useLabels from '~hooks/useLabels';
import { Asset } from '~modules/media-library/model/Asset';
import { AssetActions } from '~modules/media-library/model/AssetActions';
import { ViewVariant } from '~modules/media-library/model/ViewVariant';
import { Button } from '~modules/media-library/view/Button';
import { FavouriteButton } from '~modules/media-library/view/FavouriteButton';
import { Bubbles } from '~modules/media-library/view/MetaBubble/Bubbles';
import { TeaserMenu } from '~modules/media-library/view/Teaser/TeaserMenu';

type TeaserActionsProps = Resolve<{
  asset: Asset;
  AssetActions: AssetActions;
  variant?: ViewVariant;
  isHovering?: boolean;
}>;

export function TeaserActions({
  asset,
  variant = ViewVariant.DEFAULT,
  isHovering = false,
  AssetActions,
}: TeaserActionsProps) {
  const { btnMediaLibraryActionDownload, btnMediaLibraryActionAddToMyfiles } = useLabels([
    'btnMediaLibraryActionDownload',
    'btnMediaLibraryActionAddToMyfiles',
  ]);

  // Derived values
  const assetId = asset.id;
  const isFavourite = asset.infos.isFavourite || false;

  return (
    <div
      className={clsx(
        'ml--teaser-actions flex justify-between gap-2 flex-shrink-0',
        variant === ViewVariant.TILE_LARGE && 'mt-3 w-3/6',
        variant === ViewVariant.TILE_MEDIUM && 'p-2 w-3/6',
        variant === ViewVariant.TILE_SMALL && 'absolute bottom-0 left-0 right-0 p-2 origin-bottom transform transition',
        variant === ViewVariant.TILE_SMALL && isHovering && 'z-20',
        variant === ViewVariant.LIST && 'items-center min-w-0 px-2',
      )}
    >
      {variant === ViewVariant.LIST ? <Bubbles asset={asset} className="mr-3" /> : null}
      <div className="flex gap-2">
        {variant === ViewVariant.LIST ? (
          <FavouriteButton assetId={assetId} onToggle={AssetActions.onToggleFavourite} isFavourite={isFavourite} />
        ) : null}
        {variant === ViewVariant.LIST && !!AssetActions.onDownload ? (
          <Button
            title={btnMediaLibraryActionDownload}
            variant="icon"
            className="ml-icon-download"
            onClick={() => AssetActions.onDownload?.(assetId)}
          >
            <DownloadIcon />
          </Button>
        ) : null}
        {variant === ViewVariant.LIST && !!AssetActions.onAddToMyfiles ? (
          <Button
            title={btnMediaLibraryActionAddToMyfiles}
            variant="icon"
            className="ml-icon-symlink"
            onClick={() => AssetActions.onAddToMyfiles?.(assetId)}
          >
            <FolderSymlinkIcon />
          </Button>
        ) : null}
      </div>
      <div className="flex gap-2">
        <TeaserMenu assetId={assetId} AssetActions={AssetActions} />
      </div>
    </div>
  );
}
